import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import useFetchAllRules from "../../../hooks/useFetchAllRules";
import { Autocomplete, Chip, MenuItem, TextField } from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";

interface IAutoCompleteRuleList {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeRule: (event: ChangeEvent<any>, value: any) => void;
  currentRuleId: string | number | null | Array<string | number>; // Support both single and multiple values
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeRuleId?: boolean; // ✅ NEW: Controls whether to include rule ID in display
  isMultiple?: boolean; // ✅ NEW: Controls whether multiple selection is enabled
  filterByComparisonQueryAvailability?: boolean; // ✅ NEW: Filter by comparison_research_query_available
  filterByActiveStatus?: boolean; // ✅ NEW: Filter by is_active
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
  filterByScoreConfigAssociated?: boolean; // ✅ NEW: When true, fetch only rules without an associated score config
  currentDomainId?: string | number | null; // ✅ NEW: When set, fetch rules scoped to the given domain
}

/**
 * All rules list with auto complete dropdown functionality
 * @returns
 * Selected rule with id and name
 */
const AutoCompleteRuleList: React.FC<IAutoCompleteRuleList> = ({
  setIsLoading,
  handelOnChangeRule,
  currentRuleId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeRuleId = false, // ✅ NEW: Default to false to maintain existing behavior
  isMultiple = false, // ✅ NEW: Default to false for backward compatibility
  filterByComparisonQueryAvailability = false, // ✅ NEW: Default to false - only used in Clone dialog
  filterByActiveStatus = true, // ✅ CHANGED: Default to true - show only active rules globally
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
  filterByScoreConfigAssociated = false, // ✅ NEW: Default to false for backward compatibility
  currentDomainId, // ✅ NEW: Optional domain ID for cascading filter
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  // Build filters object based on props
  const filters = useMemo(() => {
    const filterObj: {
      is_active?: boolean;
      comparison_research_query_available?: boolean;
      is_score_config_associated?: boolean;
    } = {};

    if (filterByActiveStatus) {
      filterObj.is_active = true;
    }

    if (filterByComparisonQueryAvailability) {
      filterObj.comparison_research_query_available = false;
    }

    if (filterByScoreConfigAssociated) {
      filterObj.is_score_config_associated = false;
    }

    return Object.keys(filterObj).length > 0 ? filterObj : undefined;
  }, [
    filterByActiveStatus,
    filterByComparisonQueryAvailability,
    filterByScoreConfigAssociated,
  ]);

  // Only fetch on mount if lazyLoad is false
  const [rulesData, fetchRules, isFetching] = useFetchAllRules({
    setIsLoading,
    filters,
    fetchOnMount: !lazyLoad,
    currentDomainId,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchRules();
    }
  }, [lazyLoad, hasOpened, fetchRules]);

  // When lazy-loading with a preselected id (e.g., restored from URL),
  // fetch immediately so the selected option can be resolved on first render.
  useEffect(() => {
    const hasPreselected = Array.isArray(currentRuleId)
      ? currentRuleId.length > 0
      : !!currentRuleId;
    if (lazyLoad && !hasOpened && hasPreselected) {
      setHasOpened(true);
      fetchRules();
    }
  }, [lazyLoad, hasOpened, currentRuleId, fetchRules]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  // Compute the value based on single or multiple mode
  const computedValue = useMemo(() => {
    if (!rulesData) return isMultiple ? [] : null;

    if (isMultiple) {
      // Handle multiple selection mode
      if (Array.isArray(currentRuleId)) {
        return rulesData.filter((option) => currentRuleId.includes(option.id));
      }
      // If single value provided in multiple mode, convert to array
      if (currentRuleId) {
        const found = rulesData.find((option) => option.id === currentRuleId);
        return found ? [found] : [];
      }
      return [];
    } else {
      // Handle single selection mode
      return rulesData.find((option) => option.id === currentRuleId) || null;
    }
  }, [rulesData, currentRuleId, isMultiple]);

  // Helper function to get option label
  const getLabel = (option: any) => {
    return includeRuleId
      ? `(${option.id}) ${option.name}` || ""
      : option.name || "";
  };

  return (
    <Autocomplete
      sx={sx}
      fullWidth={isMultiple}
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      options={rulesData || []}
      value={computedValue}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) =>
        isMultiple ? (
          <div className="autocomplete-chips-direction">
            <TextField
              style={{ color: "#000000" }}
              {...params}
              label={
                <span>
                  {listLabel ? listLabel : "Rule"}
                  {required ? <span className="required-asterisk">*</span> : ""}
                </span>
              }
              placeholder={placeholder ? placeholder : "Select..."}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error}
              helperText={helperText || ""}
            />
          </div>
        ) : (
          <TextField
            style={{ color: "#000000" }}
            {...params}
            label={
              <span>
                {listLabel ? listLabel : "Rule"}
                {required ? <span className="required-asterisk">*</span> : ""}
              </span>
            }
            placeholder={placeholder ? placeholder : "Select..."}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!error}
            helperText={helperText || ""}
          />
        )
      }
      renderTags={
        isMultiple
          ? (value, getTagProps) => (
              <>
                <div className="vertical-scroll">
                  {value.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={getLabel(option)}
                    />
                  ))}
                </div>
              </>
            )
          : undefined
      }
      renderOption={
        isMultiple
          ? (props, option, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.name}`}
                value={option?.name}
                sx={{ justifyContent: "space-between" }}
              >
                {getLabel(option)}
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )
          : undefined
      }
      className={`${className}${
        isMultiple ? " form-control-autocomplete" : ""
      }`}
      loadingText="Loading..."
      onChange={(event, value) => handelOnChangeRule(event, value)}
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteRuleList;
